import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isEqual, omit } from "lodash";
import { arrayMove } from "react-sortable-hoc";
import { useToasts } from "react-toast-notifications";
import { StringParam } from "serialize-query-params";
import { useQueryParam } from "use-query-params";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { useLogger, usePrettyLogEventPage } from "@components/v2/analytics";
import { type Active, type Over } from "@dnd-kit/core";
import {
  TilesSavingState,
  type OverviewFilters,
  type OverviewSavedView,
  type PageConfiguration,
  type TileDescriptor,
} from "@features/overview-v2/types";
import {
  CREATE_HIDDEN_OVERVIEW_SAVED_VIEW_TEMPLATE,
  CREATE_OVERVIEW_SAVED_VIEW,
  DELETE_OVERVIEW_SAVED_VIEW,
  GET_OVERVIEW_SAVED_VIEWS,
  UPDATE_OVERVIEW_SAVED_VIEW,
  UPDATE_OVERVIEW_SAVED_VIEW_ORDERING,
  UPDATE_OVERVIEW_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS,
} from "@gql/overview-saved-views";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type UpdateOverviewSavedViewOrderingToAllAccountManagers,
  type UpdateOverviewSavedViewOrderingToAllAccountManagersVariables,
  type AmplitudeEventContainer,
  type AmplitudeNativeEventType,
  type CreateHiddenOverviewSavedViewTemplate,
  type CreateHiddenOverviewSavedViewTemplateVariables,
  type CreateOverviewSavedView,
  type CreateOverviewSavedViewVariables,
  type DeleteOverviewSavedView,
  type DeleteOverviewSavedViewVariables,
  type GetOverviewSavedViews,
  type UpdateOverviewSavedView,
  type UpdateOverviewSavedViewOrdering,
  type UpdateOverviewSavedViewOrderingVariables,
  type UpdateOverviewSavedViewVariables,
} from "@north-beam/nb-client-side";
import { parseInteger } from "@north-beam/nb-common/parsers";

import { useFilters, type FiltersManager } from "./use-filters";
import { useTiles, type TilesManager } from "./use-tiles";

function findSavedView(
  savedViews: OverviewSavedView[],
  id: "default" | number,
): OverviewSavedView | null {
  // Is the default string being actually used?
  if (id === "default") {
    return savedViews.length === 0 ? null : savedViews[0];
  }
  const result = savedViews.find((view) => view.id === id);
  return result === undefined ? null : result;
}

const parseSavedViews = (data: GetOverviewSavedViews) => {
  const views: OverviewSavedView[] | undefined = data?.me?.overviewSavedViews;
  if (views) {
    return views;
  }
  return null;
};

/**
 * Represents a specific part of the {@link OverviewSavedView} that is updated via an API request.
 */
type SaveEntity = "name" | "filters" | "tiles";

type PrivateSaveEntityTypes = {
  name: { name: string };
  /* This is annoying - and should be fixed by allowing the API endpoint to accept filters and
     tiles each separately, and filters as partial updates.  We shouldn't be relying on state
     values when updating the entire form of an object - it is bug prone. */
  filters: { filters: OverviewFilters; existingTiles: TileDescriptor[] };
  tiles: { existingFilters: OverviewFilters; tiles: TileDescriptor[] };
};

export type PrivateSaveEntityData<E extends SaveEntity = SaveEntity> =
  E extends SaveEntity ? PrivateSaveEntityTypes[E] : never;

type SaveEntityTypes = {
  name: string;
  filters: Partial<OverviewFilters>;
  tiles: TileDescriptor[];
};

/**
 * Represents the form of the data that is used to update an {@link OverviewSavedView} via an API
 * request:
 *
 * | { name: string }
 * | { filters: Partial<OverviewFilters> }
 * | { tiles: TileDescriptor[] }
 */
export type SaveEntityData<E extends SaveEntity = SaveEntity> =
  E extends SaveEntity ? Pick<SaveEntityTypes, E> : never;

const isSaveEntityDataOfType = <E extends SaveEntity>(
  data: SaveEntityData,
  type: E,
): data is SaveEntityData<E> =>
  (data as Record<E, unknown>)[type] !== undefined;

const isPrivateSaveEntityDataOfType = <E extends SaveEntity>(
  data: PrivateSaveEntityData,
  type: E,
): data is PrivateSaveEntityData<E> =>
  (data as Record<E, unknown>)[type] !== undefined;

type StateHandler = () => void;

type StateHandlers = [
  StateHandler,
  StateHandler | { success: StateHandler; error: StateHandler },
];

export type ModifyViewsOptions = {
  readonly successMessage?: string;
  readonly errorMessage?: string;
  readonly onError?: (e: unknown) => void;
  readonly onSuccess?: () => void;
};

export interface SavedViewsManager extends FiltersManager, TilesManager {
  readonly savedViews: OverviewSavedView[];
  readonly selectedSavedView: OverviewSavedView | null;
  readonly saveViewsSavingState: {
    readonly isLoading: boolean;
    readonly isCreating: boolean;
    readonly isDeleting: number | null;
    /**
     * Represents the request state that is associated with updating the tiles of a
     * {@link OverviewSavedView}.
     *
     * A null value means that a save is not in progress and a sufficient amount of time has passed
     * since the last successful save, or the last failed save, and so the progress of the save
     * no longer needs to be indicated in the UI.
     */
    readonly tilesSavingState: TilesSavingState | null;
    readonly isSavingFilters: boolean;
    readonly isSavingName: number | null;
  };
  readonly selectSavedView: (
    view: OverviewSavedView,
    context: {
      eventType: AmplitudeNativeEventType;
      container: AmplitudeEventContainer;
    },
  ) => void;
  readonly refetchSavedViews: () => Promise<void>;
  readonly createSavedView: (
    name: OverviewSavedView["name"],
    context: {
      container: AmplitudeEventContainer;
      eventType: AmplitudeNativeEventType;
    },
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly createEmptySavedView: (
    name: OverviewSavedView["name"],
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly updateSavedView: (
    id: number,
    data: SaveEntityData,
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly updateSavedViewFilters: (
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly updateSavedViewName: (
    id: number,
    name: string,
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly updateSavedViewOrdering: (
    active: Active,
    over: Over | null,
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly updateSavedViewOrderingToAllAccountManagers: () => Promise<void>;
  readonly deleteSavedView: (
    id: number,
    options?: ModifyViewsOptions,
  ) => Promise<void>;
  readonly createHiddenSavedViewTemplate: (
    id: number,
    options?: ModifyViewsOptions,
  ) => Promise<void>;
}

export interface UseSavedViewsConfig {
  readonly tiles: TileDescriptor[];
}

export const useSavedViews = (): SavedViewsManager => {
  const logger = useLogger({});
  const { addToast } = useToasts();
  const prettyLogEventPage = usePrettyLogEventPage();

  const timeoutRef = useRef<NodeJS.Timeout | number | null>(null);

  const [tilesSavingState, _setTilesSavingState] =
    useState<TilesSavingState | null>(null);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSavingName, setIsSavingName] = useState<number | null>(null);
  const [isSavingFilters, setIsSavingFilters] = useState(false);
  const [savedViews, setSavedViews] = useState<OverviewSavedView[] | null>(
    null,
  );

  const [_selectedSavedViewId, setSelectedSavedViewId] = useQueryParam(
    "savedViewId",
    StringParam,
    { updateType: "pushIn" },
  );

  const setTilesSavingState = useCallback((state: TilesSavingState) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    _setTilesSavingState(state);
    if (state === TilesSavingState.SUCCESS) {
      timeoutRef.current = setTimeout(() => _setTilesSavingState(null), 2000);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (savedViews && savedViews.length !== 0 && !_selectedSavedViewId) {
      setSelectedSavedViewId(String(savedViews[0].id));
    }
  }, [savedViews, _selectedSavedViewId, setSelectedSavedViewId]);

  const selectedSavedViewId = useMemo(() => {
    if (savedViews && savedViews.length !== 0 && !_selectedSavedViewId) {
      return savedViews[0].id;
    }
    return _selectedSavedViewId;
  }, [_selectedSavedViewId, savedViews]);

  const selectedSavedView = useMemo<OverviewSavedView | null>(() => {
    const parsed = z.coerce
      .number()
      .nullable()
      .optional()
      .parse(selectedSavedViewId);
    if (parsed && savedViews) {
      const result = findSavedView(savedViews, parsed);
      if (!result) {
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          `Could not find saved view for ID ${selectedSavedViewId} in state.`,
        );
      }
      return result;
    }
    return null;
  }, [selectedSavedViewId, savedViews]);

  const initialLoad = useRef(true);

  useEffect(() => {
    if (initialLoad.current && selectedSavedView) {
      const pc = selectedSavedView.config.pageConfiguration;
      const pageContext = {
        dateRange: pc.dateRange,
        attributionModel: pc.attributionModel,
        attributionWindow: pc.attributionWindow,
        accountingMode: pc.accountingMode,
        timeGranularity: pc.timeGranularity,
        compareDateRange: pc.compareDateRange,
        level: pc.level,
        savedVewId: selectedSavedView.id,
      };

      prettyLogEventPage({
        domain: "Initial state",
        action: "onMount",
        additionalActionInfo: "Initial state",
        data: {
          pageContext,
        },
      });

      initialLoad.current = false;
    }
  }, [selectedSavedView]);

  const { filters, ...filtersManager } = useFilters({
    selectedSavedView,
  });

  const { loading: isLoading, refetch: _refetchSavedViews } =
    useNorthbeamQuery<GetOverviewSavedViews>(GET_OVERVIEW_SAVED_VIEWS, {
      /* It is important that the no-cache be applied such that the saved views are reloaded when
         the page changes.  If the results are cached, setting a saved view as the default view
         (the first), changing the page, and then reloading the overview page again causes the
         reordering change not to be visible.  This is because the ordering is optimistically
         updated in state. */
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        const views: OverviewSavedView[] | null = parseSavedViews(data);
        if (views) {
          setSavedViews(views);
        }
      },
      onError: (error) => {
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          `There was an error loading saved views:\n${JSON.stringify(error)}`,
        );
        addToast("There was an error loading your saved views.", {
          appearance: "error",
        });
      },
    });

  const refetchSavedViews = useCallback(async () => {
    const { data } = await _refetchSavedViews();
    const views = parseSavedViews(data);
    setSavedViews(views);
  }, [_refetchSavedViews]);

  const [createOverviewSavedViewMutation] = useNorthbeamMutation<
    CreateOverviewSavedView,
    CreateOverviewSavedViewVariables
  >(CREATE_OVERVIEW_SAVED_VIEW);

  const [updateOverviewSavedViewMutation] = useNorthbeamMutation<
    UpdateOverviewSavedView,
    UpdateOverviewSavedViewVariables
  >(UPDATE_OVERVIEW_SAVED_VIEW);

  const [updateOverviewSavedViewOrderingMutation] = useNorthbeamMutation<
    UpdateOverviewSavedViewOrdering,
    UpdateOverviewSavedViewOrderingVariables
  >(UPDATE_OVERVIEW_SAVED_VIEW_ORDERING);

  const [updateOverviewSavedViewOrderinToAllAccountManagersMutation] =
    useNorthbeamMutation<
      UpdateOverviewSavedViewOrderingToAllAccountManagers,
      UpdateOverviewSavedViewOrderingToAllAccountManagersVariables
    >(UPDATE_OVERVIEW_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS);

  const [deleteOverviewSavedViewMutation] = useNorthbeamMutation<
    DeleteOverviewSavedView,
    DeleteOverviewSavedViewVariables
  >(DELETE_OVERVIEW_SAVED_VIEW);

  const [createHiddenSavedViewTemplateMutation] = useNorthbeamMutation<
    CreateHiddenOverviewSavedViewTemplate,
    CreateHiddenOverviewSavedViewTemplateVariables
  >(CREATE_HIDDEN_OVERVIEW_SAVED_VIEW_TEMPLATE);

  const _createSavedView = useCallback(
    /* Allow the tiles to be dynamically provided in cases where we are creating the saved view from
       an unsaved, stashed state (which occurs when the tiles are modified in a default view and the
       user is prompted to create a new view to persist the changes). */
    async (
      name: OverviewSavedView["name"],
      ts: Omit<TileDescriptor, "id">[],
      context: {
        container: AmplitudeEventContainer;
        eventType: AmplitudeNativeEventType;
      } | null,
      options?: ModifyViewsOptions,
    ) => {
      setIsCreating(true);
      let result: Awaited<
        ReturnType<typeof createOverviewSavedViewMutation>
      > | null = null;
      try {
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
        const { dateRangeInterval, compareDateRangeInterval, ...rest } =
          filters;
        result = await createOverviewSavedViewMutation({
          variables: {
            name,
            config: {
              pageConfiguration: {
                ...rest,
                tiles: ts.map((e) => ({ ...e, id: uuidv4() })),
              },
            },
          },
        });
      } catch (e) {
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          `There was an error creating the saved view:\n${JSON.stringify(e)}`,
        );
        if (options?.errorMessage) {
          addToast(options.errorMessage, { appearance: "error" });
        }
        setIsCreating(false);
        options?.onError?.(e);
      }
      if (result) {
        const createdView = result?.data?.createOverviewSavedView;
        if (createdView) {
          /* Its important to refetch the saved views first so that the newly created view is in
             state. */
          await refetchSavedViews();
          setSelectedSavedViewId(createdView.id);
          if (context) {
            logger.event.createOverviewSavedView(context.eventType, {
              container: context.container,
              eventDetails: {
                savedView: {
                  id: parseInteger(createdView.id, {}),
                  name: name,
                },
                // savedViewFilters: {
                //   accountingMode:
                //     createdView.config.pageConfiguration.accountingMode,
                //   attributionModel:
                //     createdView.config.pageConfiguration.attributionModel,
                //   /* This assertion is only necessary because currently we do not use the enum
                //      type in the API response - just a plain string. */
                //   attributionWindow: AttributionWindows.assert(
                //     createdView.config.pageConfiguration.attributionWindow,
                //   ),
                //   timeGranularity:
                //     createdView.config.pageConfiguration.timeGranularity,
                //   dateRange: createdView.config.pageConfiguration.dateRange,
                //   compareDateRange:
                //     createdView.config.pageConfiguration.compareDateRange,
                // },
              },
            });
          }
        } else {
          /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
          console.error(
            "Corrupted/malformed result from API received while creating saved view.",
          );
        }
        setIsCreating(false);
        if (options?.successMessage) {
          addToast(options.successMessage, { appearance: "success" });
        }
        options?.onSuccess?.();
      }
    },
    [
      createOverviewSavedViewMutation,
      refetchSavedViews,
      setSelectedSavedViewId,
      addToast,
      filters,
      logger.event,
    ],
  );

  const _updateSavedView = useCallback(
    async (
      id: number,
      data: PrivateSaveEntityData,
      options?: ModifyViewsOptions,
    ) => {
      type UpdateSavedViewMutationData = {
        name?: string;
        config?: { pageConfiguration: PageConfiguration };
      };

      let updateData: UpdateSavedViewMutationData;
      let stateHandlers: StateHandlers;
      if (isPrivateSaveEntityDataOfType(data, "filters")) {
        /* This is annoying - and should be fixed by allowing the API endpoint to accept filters and
           tiles each separately, and filters as partial updates.  We shouldn't be relying on state
           values when updating the entire form of an object - it is bug prone. */
        updateData = {
          config: {
            pageConfiguration: {
              ...data.filters,
              tiles: data.existingTiles,
            },
          },
        };
        stateHandlers = [
          () => setIsSavingFilters(true),
          () => setIsSavingFilters(false),
        ];
      } else if (isPrivateSaveEntityDataOfType(data, "name")) {
        updateData = { name: data.name };
        stateHandlers = [() => setIsSavingName(id), () => setIsSavingName(id)];
      } else if (isPrivateSaveEntityDataOfType(data, "tiles")) {
        /* This is annoying - and should be fixed by allowing the API endpoint to accept filters and
           tiles each separately, and filters as partial updates.  We shouldn't be relying on state
           values when updating the entire form of an object - it is bug prone. */
        updateData = {
          config: {
            pageConfiguration: {
              ...data.existingFilters,
              tiles: data.tiles,
            },
          },
        };
        stateHandlers = [
          () => setTilesSavingState(TilesSavingState.SAVING),
          {
            /* TODO: When there is an error, we need to rollback the optimistically updated tiles
               changes that are represented in the query parameters. */
            error: () => setTilesSavingState(TilesSavingState.ERROR),
            success: () => setTilesSavingState(TilesSavingState.SUCCESS),
          },
        ];
      } else {
        throw new TypeError("Invalid data provided to update method!");
      }

      stateHandlers[0]();
      try {
        await updateOverviewSavedViewMutation({
          variables: { id, ...updateData },
        });
      } catch (e) {
        if (typeof stateHandlers[1] === "function") {
          stateHandlers[1]();
        } else {
          stateHandlers[1].error();
        }
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          `There was an error updating the saved view:\n${JSON.stringify(e)}`,
        );
        if (options?.errorMessage) {
          addToast(options.errorMessage, { appearance: "error" });
        }
        options?.onError?.(e);
      }
      // TODO: We might need to properly error handle this.
      await refetchSavedViews();
      if (typeof stateHandlers[1] === "function") {
        stateHandlers[1]();
      } else {
        stateHandlers[1].success();
      }
      if (options?.successMessage) {
        addToast(options.successMessage, { appearance: "success" });
      }
      options?.onSuccess?.();
    },
    [
      addToast,
      refetchSavedViews,
      setTilesSavingState,
      updateOverviewSavedViewMutation,
    ],
  );

  const { tiles, ...tilesManager } = useTiles({
    updateSavedView: (
      id: number,
      ts: TileDescriptor[],
      options?: ModifyViewsOptions,
    ) => _updateSavedView(id, { tiles: ts, existingFilters: filters }, options),
    createSavedView: _createSavedView,
    selectedSavedView,
  });

  const createSavedView = useCallback(
    /* Allow the tiles to be dynamically provided in cases where we are creating the saved view from
       an unsaved, stashed state (which occurs when the tiles are modified in a default view and the
       user is prompted to create a new view to persist the changes). */
    (
      name: OverviewSavedView["name"],
      context: {
        container: AmplitudeEventContainer;
        eventType: AmplitudeNativeEventType;
      },
      options?: ModifyViewsOptions,
    ) => _createSavedView(name, tiles, context, options),
    [_createSavedView, tiles],
  );

  const createEmptySavedView = useCallback(
    (name: OverviewSavedView["name"], options?: ModifyViewsOptions) =>
      _createSavedView(name, [], null, options),
    [_createSavedView],
  );

  const updateSavedView = useCallback(
    (id: number, data: SaveEntityData, options?: ModifyViewsOptions) => {
      if (isSaveEntityDataOfType(data, "filters")) {
        return _updateSavedView(
          id,
          {
            filters: { ...filters, ...data.filters },
            existingTiles: tiles,
          },
          options,
        );
      } else if (isSaveEntityDataOfType(data, "tiles")) {
        return _updateSavedView(
          id,
          {
            existingFilters: filters,
            tiles: data.tiles,
          },
          options,
        );
      } else if (isSaveEntityDataOfType(data, "name")) {
        return _updateSavedView(id, { name: data.name }, options);
      } else {
        throw new TypeError("Invalid data provided to update method!");
      }
    },
    [filters, tiles, _updateSavedView],
  );

  const updateSavedViewName = useCallback(
    async (id: number, name: string, options?: ModifyViewsOptions) =>
      updateSavedView(
        id,
        { name },
        {
          errorMessage: "There was an error updating the view's name.",
          ...options,
        },
      ),
    [updateSavedView],
  );

  const updateSavedViewFilters = useCallback(
    async (options?: ModifyViewsOptions) => {
      if (selectedSavedView) {
        return await updateSavedView(
          selectedSavedView.id,
          {
            filters: omit(filters, [
              "compareDateRangeInterval",
              "dateRangeInterval",
            ]),
          },
          {
            errorMessage: "There was an error updating the view's filters.",
            ...options,
          },
        );
      } else {
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          "Cannot update saved view config - there is not a selected saved view in state.",
        );
      }
    },
    [updateSavedView, selectedSavedView, filters],
  );

  const updateSavedViewOrderingToAllAccountManagers = useCallback(async () => {
    if (savedViews) {
      await updateOverviewSavedViewOrderinToAllAccountManagersMutation({
        variables: { ids: savedViews.map((view) => view.id) },
      });
    }
  }, [savedViews, updateOverviewSavedViewOrderinToAllAccountManagersMutation]);

  const updateSavedViewOrdering = useCallback(
    async (
      active: Active,
      over: Over | null,
      options?: Parameters<SavedViewsManager["updateSavedViewOrdering"]>[2],
    ) => {
      if (savedViews) {
        const oldIndex = savedViews.findIndex(({ id }) => id === active.id);
        const newIndex = savedViews.findIndex(({ id }) => id === over?.id);
        const newOrdering = arrayMove(savedViews, oldIndex, newIndex);

        /* Optimistically update the ordering in the UI state - if there is an error reordering it
           will remain in the optimistically updated state until a reload.  We want to do this
           regardless of the equality check for more consistent behavior. */
        setSavedViews(newOrdering);

        if (
          !isEqual(
            newOrdering.map((view) => view.id),
            savedViews.map((view) => view.id),
          )
        ) {
          /* Perform the persistent update to ordering in the background.  If it fails, refetch the
             saved views to revert ordering. */
          try {
            await updateOverviewSavedViewOrderingMutation({
              variables: { ids: newOrdering.map((view) => view.id) },
            });
          } catch (e) {
            /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
            console.error(
              `There was an error reordering the views:\n${JSON.stringify(e)}`,
            );
            /* The ordering of the views is already updated in state, so we do not need to refetch
               the saved views if the request is successful - in fact, performing the refetch can
               cause glitches in the case that multiple views are moved relatively quickly, because
               when the first refetch request finishes, it may temporarily wipe the update in
               ordering state that occurred from the subsequent view.

               However, if the request is not successful, we want to update the views in state such
               that the reordering that was optimistically applied is reversed. */
            await refetchSavedViews();
            if (options?.errorMessage) {
              addToast(options.errorMessage, { appearance: "error" });
            }
            options?.onError?.(e);
            return;
          }
          if (options?.successMessage) {
            addToast(options.successMessage, { appearance: "success" });
          }
          options?.onSuccess?.();
        }
      }
    },
    [
      savedViews,
      updateOverviewSavedViewOrderingMutation,
      refetchSavedViews,
      addToast,
    ],
  );

  const deleteSavedView = useCallback(
    async (id: number, options?: ModifyViewsOptions) => {
      setIsDeleting(id);
      try {
        await deleteOverviewSavedViewMutation({ variables: { id } });
      } catch (e) {
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          `There was an error deleting the saved view:\n${JSON.stringify(e)}`,
        );
        setIsDeleting(null);
        addToast(
          options?.errorMessage || "There was an error deleting the view.",
          { appearance: "error" },
        );
        options?.onError?.(e);
      }
      await refetchSavedViews();
      setIsDeleting(null);
      if (options?.successMessage) {
        addToast(options.successMessage, { appearance: "success" });
      }
      options?.onSuccess?.();
    },
    [addToast, deleteOverviewSavedViewMutation, refetchSavedViews],
  );

  const createHiddenSavedViewTemplate = useCallback(
    async (id: number, options?: ModifyViewsOptions) => {
      setIsDeleting(id);
      try {
        await createHiddenSavedViewTemplateMutation({ variables: { id } });
      } catch (e) {
        /* eslint-disable-next-line no-console -- Need to figure out FE logging. */
        console.error(
          `There was an error deleting the saved view:\n${JSON.stringify(e)}`,
        );
        setIsDeleting(null);
        addToast(
          options?.errorMessage || "There was an error deleting the view.",
          { appearance: "error" },
        );
        options?.onError?.(e);
      }
      await refetchSavedViews();
      setIsDeleting(null);
      if (options?.successMessage) {
        addToast(options.successMessage, { appearance: "success" });
      }
      options?.onSuccess?.();
    },
    [addToast, createHiddenSavedViewTemplateMutation, refetchSavedViews],
  );

  return {
    ...filtersManager,
    ...tilesManager,
    filters,
    tiles,
    deleteSavedView,
    updateSavedViewFilters,
    updateSavedView,
    updateSavedViewName,
    updateSavedViewOrdering,
    updateSavedViewOrderingToAllAccountManagers,
    selectSavedView: (
      view: OverviewSavedView,
      context: {
        eventType: AmplitudeNativeEventType;
        container: AmplitudeEventContainer;
      },
    ) => {
      setSelectedSavedViewId(String(view.id));

      logger.event.selectOverviewSavedView(context.eventType, {
        container: context.container,
        eventDetails: {
          savedView: {
            id: parseInteger(view.id, {}),
            name: view.name,
            isTemplate: view.is_template,
          },
          savedViewFilters: {
            accountingMode: view.config.pageConfiguration.accountingMode,
            attributionModel: view.config.pageConfiguration.attributionModel,
            /* This assertion is only necessary because currently we do not use the enum
               type in the API response - just a plain string. */
            attributionWindow: view.config.pageConfiguration.attributionWindow,
            timeGranularity: view.config.pageConfiguration.timeGranularity,
            dateRange: view.config.pageConfiguration.dateRange,
            compareDateRange: view.config.pageConfiguration.compareDateRange,
          },
        },
      });
    },
    createSavedView,
    createEmptySavedView,
    refetchSavedViews: () => refetchSavedViews(),
    saveViewsSavingState: {
      isLoading,
      isCreating,
      isSavingFilters,
      isSavingName,
      tilesSavingState,
      isDeleting,
    },
    createHiddenSavedViewTemplate,
    savedViews: savedViews || [],
    selectedSavedView,
  };
};
